import type { IconName } from '@cbhq/cds-common';
import type { MessageDescriptor } from '@cbhq/i18n';
import { defineMessages } from '@cbhq/i18n';

import type { AuditLogRowEntry } from '../types';

export type AuditEventMeta = {
  message: MessageDescriptor;
  iconName: IconName;
};

const messages = defineMessages({
  memberInvited: {
    id: 'Activity.audit.event.memberInvited',
    defaultMessage: 'Member invited',
    description: 'Audit log row title for a member invite event',
  },
  memberInviteAccepted: {
    id: 'Activity.audit.event.memberInviteAccepted',
    defaultMessage: 'Invite accepted',
    description: 'Audit log row title for a member invite accepted event',
  },
  memberInviteDeclined: {
    id: 'Activity.audit.event.memberInviteDeclined',
    defaultMessage: 'Invite declined',
    description: 'Audit log row title for a member invite declined event',
  },
  memberInviteCancelled: {
    id: 'Activity.audit.event.memberInviteCancelled',
    defaultMessage: 'Invite cancelled',
    description: 'Audit log row title for a member invite cancelled event',
  },
  memberAdded: {
    id: 'Activity.audit.event.memberAdded',
    defaultMessage: 'Member added',
    description: 'Audit log row title for a member added event',
  },
  memberRoleUpdated: {
    id: 'Activity.audit.event.memberRoleUpdated',
    defaultMessage: 'Member role updated',
    description: 'Audit log row title for a member role updated event',
  },
  memberRemoved: {
    id: 'Activity.audit.event.memberRemoved',
    defaultMessage: 'Member removed',
    description: 'Audit log row title for a member removed event',
  },
  legalEntityJoinRequested: {
    id: 'Activity.audit.event.legalEntityJoinRequested',
    defaultMessage: 'User requested to join Legal Entity',
    description: 'Audit log row title for a legal entity join request event',
  },
  auditLogViewed: {
    id: 'Activity.audit.event.auditLogViewed',
    defaultMessage: 'Viewed Activity page',
    description: 'Audit log row title for an audit log viewed event',
  },
  auditLogExported: {
    id: 'Activity.audit.event.auditLogExported',
    defaultMessage: 'Audit log exported',
    description: 'Audit log row title for an audit log exported event',
  },
});

const AUDIT_EVENT_META: Record<string, AuditEventMeta> = {
  'member.invited': { message: messages.memberInvited, iconName: 'email' },
  'member.invite_accepted': { message: messages.memberInviteAccepted, iconName: 'following' },
  'member.invite_declined': { message: messages.memberInviteDeclined, iconName: 'circleCross' },
  'member.invite_cancelled': { message: messages.memberInviteCancelled, iconName: 'circleCross' },
  'member.added': { message: messages.memberAdded, iconName: 'followAdd' },
  'member.role_updated': { message: messages.memberRoleUpdated, iconName: 'pFPS' },
  'member.removed': { message: messages.memberRemoved, iconName: 'unfollowPeople' },
  'legal_entity.join_requested': {
    message: messages.legalEntityJoinRequested,
    iconName: 'corporation',
  },
  'audit_log.viewed': { message: messages.auditLogViewed, iconName: 'pulse' },
  'audit_log.exported': { message: messages.auditLogExported, iconName: 'download' },
};

export function getAuditEventMeta(eventType: string): AuditEventMeta | undefined {
  return AUDIT_EVENT_META[eventType];
}

type FormatAuditEventMessage = (descriptor: MessageDescriptor) => string;

export function getAuditEventTitle(
  eventType: string,
  formatMessage: FormatAuditEventMessage,
): string {
  const eventMeta = getAuditEventMeta(eventType);
  return eventMeta ? formatMessage(eventMeta.message) : eventType;
}

export type AuditEventTypeOption = {
  value: string;
  label: string;
};

export function getAuditEventTypeOptions(
  formatMessage: FormatAuditEventMessage,
): AuditEventTypeOption[] {
  return Object.entries(AUDIT_EVENT_META)
    .map(([value, meta]) => ({ value, label: formatMessage(meta.message) }))
    .sort((a, b) => a.label.localeCompare(b.label));
}

export type AuditLogTimestampDisplay = {
  date: string;
  time: string;
};

export function formatAuditLogTimestamp(timestamp: string): AuditLogTimestampDisplay {
  const date = new Date(timestamp);

  const formattedDate = new Intl.DateTimeFormat('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  })
    .format(date)
    .replace(',', '');

  const formattedTime = new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZone: 'UTC',
  })
    .format(date)
    .replace(' ', '');

  return {
    date: formattedDate,
    time: formattedTime,
  };
}

export function formatAuditLogLocation({
  city,
  country_code,
}: AuditLogRowEntry['actor_context']): string {
  return [city, country_code].filter(Boolean).join(', ');
}

export function formatAuditLogUserAgent(userAgent: string): string {
  const match = userAgent.match(/^(Mozilla\/[\d.]+)/);
  return match ? match[1].replace('/', ' ') : userAgent;
}

export function formatAuditLogDevice(userAgent: string): string {
  const parts: string[] = [];

  if (/Mac OS X/i.test(userAgent)) {
    parts.push('Mac OS');
  } else if (/Windows NT/i.test(userAgent)) {
    parts.push('Windows');
  } else if (/Android/i.test(userAgent)) {
    parts.push('Android');
  } else if (/Linux/i.test(userAgent)) {
    parts.push('Linux');
  }

  const chrome = userAgent.match(/Chrome\/([\d.]+)/);
  const firefox = userAgent.match(/Firefox\/([\d.]+)/);
  const edge = userAgent.match(/Edg\/([\d.]+)/);

  if (edge) {
    parts.push(`Edge ${edge[1].split('.')[0]}`);
  } else if (chrome && !/Edg\//.test(userAgent)) {
    parts.push(`Chrome ${chrome[1].split('.')[0]}`);
  } else if (firefox) {
    parts.push(`Firefox ${firefox[1].split('.')[0]}`);
  }

  return parts.length > 0 ? parts.join(' · ') : userAgent;
}
